import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill's default styling
import Select from "react-select";
import Http from "../../../Common/Http";

// Sample options for Select components
const assignedByOptions = [
  { value: "user1", label: "User One" },
  { value: "user2", label: "User Two" },
  { value: "user3", label: "User Three" },
];

const statusOptions = [
  { value: "done", label: "Done" },
  { value: "inProgress", label: "In Progress" },
  { value: "pending", label: "Pending" },
];

const assignUsersOptions = [
  { value: "user1", label: "User One" },
  { value: "user2", label: "User Two" },
  { value: "user3", label: "User Three" },
  { value: "user4", label: "User Four" },
];

const taskTypeOptions = [
  { value: "task", label: "Task" },
  { value: "bug", label: "Bug" },
  { value: "re_task", label: "ReTask" },
  { value: "maintenance", label: "Maintenance" },
];

const linkedIssueTypes = [
  { value: "blockedBy", label: "Blocked By" },
  { value: "blocks", label: "Blocks" },
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }], // Header levels
    ["bold", "italic", "underline"], // Formatting styles
    [{ color: [] }, { background: [] }], // Text and background color
    [{ list: "ordered" }, { list: "bullet" }], // Lists
    ["link"], // Hyperlinks
    ["clean"], // Remove formatting
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"];

const Edit = ({ setVisible, fetchData, taskId, onSuccess }) => {
  const [inputs, setInputs] = useState({
    title: "",
    description: "",
    story_id: null,
    project_id: null,
    task_type_id: null,
    points: 0,
    label_id: null,
    status: "To Do",
    developer_ids: [],
    start_date: "",
    end_date: "",
    estimated_time: "",
  });

  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(true);
  const [error, setError] = useState("");

  // Options for select dropdowns
  const [projects, setProjects] = useState([]);
  const [stories, setStories] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [labels, setLabels] = useState([]);

  // Status options for dropdown
  const statusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Fetch task data for editing
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!taskId) return;

      setTaskLoading(true);
      setError("");

      try {
        const response = await Http.get(`/task/${taskId}`);
        if (response.data && response.data.data) {
          const taskData = response.data.data;

          setInputs({
            title: taskData.title || "",
            description: taskData.description || "",
            story_id: taskData.story_id || null,
            project_id: taskData.project_id || null,
            task_type_id: taskData.task_type_id || null,
            points: taskData.points || 0,
            label_id: taskData.label_id || null,
            status: taskData.status || "To Do",
            developer_ids: taskData.developers?.map((dev) => dev.id) || [],
            start_date: taskData.start_date
              ? typeof taskData.start_date === "string" && taskData.start_date.includes("T")
                ? taskData.start_date.split("T")[0]
                : taskData.start_date
              : "",
            end_date: taskData.end_date
              ? typeof taskData.end_date === "string" && taskData.end_date.includes("T")
                ? taskData.end_date.split("T")[0]
                : taskData.end_date
              : "",
            estimated_time: taskData.estimated_time || "",
          });
        } else {
          setError("Failed to load task data");
        }
      } catch (error) {
        console.error("Error fetching task data:", error);
        setError(error.response?.data?.message || "Failed to load task data. Please try again.");
      } finally {
        setTaskLoading(false);
      }
    };

    fetchTaskData();
  }, [taskId]);

  // Fetch initial data for dropdowns
  useEffect(() => {
    const fetchFormData = async () => {
      setFormLoading(true);
      let hasError = false;

      try {
        // Fetch projects
        const projectsRes = await Http.get("/project");
        if (projectsRes.data && projectsRes.data.data) {
          setProjects(
            projectsRes.data.data.map((project) => ({
              label: project.title,
              value: project.id,
            }))
          );
        } else {
          console.warn("No projects data found");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        hasError = true;
      }

      try {
        // Fetch stories
        const storiesRes = await Http.get("/story");
        if (storiesRes.data && storiesRes.data.data) {
          setStories(
            storiesRes.data.data.map((story) => ({
              label: story.title,
              value: story.id,
              project_id: story.project_id,
            }))
          );
        } else {
          console.warn("No stories data found");
        }
      } catch (error) {
        console.error("Error fetching stories:", error);
        // Continue even if stories can't be fetched
      }

      try {
        // Fetch developers
        const developersRes = await Http.get("/staff-department-wise/30");
        if (Array.isArray(developersRes.data)) {
          setDevelopers(
            developersRes.data.map((dev) => ({
              label: dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`,
              value: dev.id,
            }))
          );
        } else {
          console.warn("No developers data found");
        }
      } catch (error) {
        console.error("Error fetching developers:", error);
        // Continue even if developers can't be fetched
      }

      try {
        // Fetch task types
        const taskTypesRes = await Http.get("/task-type");
        if (taskTypesRes.data && taskTypesRes.data.data) {
          setTaskTypes(
            taskTypesRes.data.data.map((type) => ({
              label: type.task_type,
              value: type.id,
              icon: type.icon,
              color: type.color,
            }))
          );
        } else {
          console.warn("No task types data found");
        }
      } catch (error) {
        console.error("Error fetching task types:", error);
        hasError = true;
      }

      try {
        // Fetch labels
        const labelsRes = await Http.get("/label");
        if (labelsRes.data && labelsRes.data.data) {
          setLabels(
            labelsRes.data.data.map((label) => ({
              label: label.name,
              value: label.id,
              color: label.color,
            }))
          );
        } else {
          console.warn("No labels data found");
        }
      } catch (error) {
        console.error("Error fetching labels:", error);
        hasError = true;
      }

      if (hasError) {
        setError("Some form data could not be loaded. You may still edit this task but some options might be missing.");
      }

      setFormLoading(false);
    };

    fetchFormData();
  }, []);

  // Handle basic input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Handle rich text editor changes
  const handleQuillChange = (content) => {
    setInputs((prev) => ({ ...prev, description: content }));
  };

  // Handle Select dropdowns
  const handleSelectChange = (selectedOption, { name }) => {
    setInputs((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : null }));

    // If story is selected, also set the project_id from the story
    if (name === "story_id" && selectedOption) {
      const story = stories.find((s) => s.value === selectedOption.value);
      if (story && story.project_id) {
        setInputs((prev) => ({ ...prev, project_id: story.project_id }));
      }
    }
  };

  // Handle multi-select for developers
  const handleDevelopersChange = (selectedOptions) => {
    setInputs((prev) => ({
      ...prev,
      developer_ids: selectedOptions ? selectedOptions.map((option) => option.value) : [],
    }));
  };

  // Custom template for colored items (task types and labels)
  const coloredItemTemplate = (option) => {
    if (!option) return null;

    return (
      <div className="d-flex align-items-center">
        <div
          style={{
            width: "15px",
            height: "15px",
            backgroundColor: option.color || "#ccc",
            borderRadius: "3px",
            marginRight: "8px",
          }}
        ></div>
        {option.icon && <i className={option.icon} style={{ marginRight: "8px" }}></i>}
        <span>{option.label}</span>
      </div>
    );
  };

  // Form validation and submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Form validation
    if (!inputs.title.trim()) {
      setError("Task title is required");
      setLoading(false);
      return;
    }

    if (!inputs.project_id && !inputs.story_id) {
      setError("Either a project or a story must be selected");
      setLoading(false);
      return;
    }

    if (!inputs.start_date) {
      setError("Start date is required");
      setLoading(false);
      return;
    }

    if (!inputs.end_date) {
      setError("End date is required");
      setLoading(false);
      return;
    }

    if (new Date(inputs.end_date) < new Date(inputs.start_date)) {
      setError("End date cannot be earlier than start date");
      setLoading(false);
      return;
    }

    try {
      const response = await Http.put(`/task/${taskId}`, inputs);
      console.log("Task updated successfully:", response.data);

      // If we have a response with data and an onSuccess callback is provided
      if (response.data && onSuccess) {
        onSuccess(response.data.data || response.data);
      }
      // For backward compatibility
      else if (setVisible && fetchData) {
        setVisible(false);
        fetchData();
      }
    } catch (error) {
      console.error("Error updating task:", error);
      setError(error.response?.data?.message || "Failed to update task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading || formLoading || taskLoading ? (
        <div className="text-center p-5">
          <ProgressSpinner />
          <p className="mt-2">{taskLoading ? "Loading task data..." : formLoading ? "Loading form data..." : "Updating task..."}</p>
        </div>
      ) : (
        <div className="container-fluid">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              {/* Task Title */}
              <div className="col-lg-12">
                <label htmlFor="title" className="form-label">
                  Task Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  id="title"
                  value={inputs.title}
                  onChange={handleInputChange}
                  placeholder="Enter task title"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              {/* Story */}
              <div className="col-md-6">
                <label htmlFor="story_id" className="form-label">
                  Story (Optional)
                </label>
                <Select
                  id="story_id"
                  name="story_id"
                  value={stories.find((option) => option.value === inputs.story_id) || null}
                  options={stories}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a story"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                />
                <small className="text-muted">If selected, this task will be part of the story</small>
              </div>

              {/* Project */}
              <div className="col-md-6">
                <label htmlFor="project_id" className="form-label">
                  Project {!inputs.story_id && <span className="text-danger">*</span>}
                </label>
                <Select
                  id="project_id"
                  name="project_id"
                  value={projects.find((option) => option.value === inputs.project_id) || null}
                  options={projects}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a project"
                  isDisabled={!!inputs.story_id}
                  className="basic-select"
                  classNamePrefix="select"
                />
                <small className="text-muted">
                  {inputs.story_id ? "Project is determined by the selected story" : "Required if no story is selected"}
                </small>
              </div>
            </div>

            <div className="row mb-3">
              {/* Description */}
              <div className="col-lg-12">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <ReactQuill
                  theme="snow"
                  value={inputs.description}
                  onChange={handleQuillChange}
                  placeholder="Enter task description"
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "150px", marginBottom: "40px" }}
                />
              </div>
            </div>

            <div className="row mb-3">
              {/* Task Type */}
              <div className="col-md-6">
                <label htmlFor="task_type_id" className="form-label">
                  Task Type
                </label>
                <Select
                  id="task_type_id"
                  name="task_type_id"
                  value={taskTypes.find((option) => option.value === inputs.task_type_id) || null}
                  options={taskTypes}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select task type"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                  formatOptionLabel={coloredItemTemplate}
                />
              </div>

              {/* Label */}
              <div className="col-md-6">
                <label htmlFor="label_id" className="form-label">
                  Label
                </label>
                <Select
                  id="label_id"
                  name="label_id"
                  value={labels.find((option) => option.value === inputs.label_id) || null}
                  options={labels}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a label"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                  formatOptionLabel={coloredItemTemplate}
                />
              </div>
            </div>

            <div className="row mb-3">
              {/* Status */}
              <div className="col-md-6">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <Select
                  id="status"
                  name="status"
                  value={statusOptions.find((option) => option.value === inputs.status) || statusOptions[0]}
                  options={statusOptions}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select status"
                  className="basic-select"
                  classNamePrefix="select"
                />
              </div>

              {/* Points */}
              <div className="col-md-6">
                <label htmlFor="points" className="form-label">
                  Points
                </label>
                <input type="number" className="form-control" name="points" id="points" value={inputs.points} onChange={handleInputChange} min="0" />
              </div>
            </div>

            <div className="row mb-3">
              {/* Estimated Time */}
              <div className="col-md-6">
                <label htmlFor="estimated_time" className="form-label">
                  Estimated Time
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="estimated_time"
                  id="estimated_time"
                  value={inputs.estimated_time}
                  onChange={handleInputChange}
                  placeholder="e.g., 2 hours, 3 days"
                />
              </div>

              {/* Assign Developers */}
              <div className="col-md-6">
                <label htmlFor="developer_ids" className="form-label">
                  Assign Developers
                </label>
                <Select
                  id="developer_ids"
                  isMulti
                  name="developer_ids"
                  options={developers}
                  value={developers.filter((dev) => inputs.developer_ids.includes(dev.value))}
                  onChange={handleDevelopersChange}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select developers"
                />
              </div>
            </div>

            <div className="row mb-3">
              {/* Start Date */}
              <div className="col-md-6">
                <label htmlFor="start_date" className="form-label">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="start_date"
                  id="start_date"
                  value={inputs.start_date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* End Date */}
              <div className="col-md-6">
                <label htmlFor="end_date" className="form-label">
                  End Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="end_date"
                  id="end_date"
                  value={inputs.end_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <button type="submit" className="btn btn-primary float-end" disabled={loading}>
                  {loading ? (
                    <span>
                      <i className="fa fa-spinner fa-spin"></i> Updating...
                    </span>
                  ) : (
                    "Update Task"
                  )}
                </button>
                <button type="button" className="btn btn-secondary float-end me-2" onClick={() => setVisible(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Edit;
